import type { AppState, Cartela, DrawEvent, RevealOrder } from '../shared/types';

/**
 * Motor de sorteio — funções puras, sem DOM.
 *
 * Justiça comprovável ("provably fair"):
 *  1. Gera-se uma semente aleatória com crypto.getRandomValues.
 *  2. Mostra-se na tela o SELO = SHA-256(semente) ANTES de sortear.
 *  3. Ganhador = pool[ SHA-256(semente|prêmio) mod pool.length ] → uniforme.
 *  4. Ao terminar revela-se a semente: qualquer um recalcula e confere.
 *
 * >>> Em produção este módulo roda no servidor. A API do Node tem as mesmas
 * primitivas (crypto.randomBytes / createHash), então a portabilidade é direta.
 */

export function randomSeed(): string {
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  return Array.from(a, b => b.toString(16).padStart(2, '0')).join('');
}

export async function sha256(str: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf), b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Bolão elegível: cartelas ativas, excluindo quem já ganhou neste sorteio
 * quando `onePerLead` está ligado.
 */
/**
 * Bolão de um sorteio (mesma regra do servidor, `EventsService.pool`):
 * - só cartelas vivas (`active`);
 * - semanal: cartelas que ainda não jogaram nenhuma semanal;
 * - mensal: todas as vivas do mês (as que já jogaram a semanal continuam);
 * - "um prêmio por jogador": quem já ganhou neste sorteio sai.
 */
export function eligiblePool(state: AppState, event: DrawEvent | null): Cartela[] {
  const winners = new Set(
    state.draws.filter(d => !event || !d.eventId || d.eventId === event.id).map(d => d.leadId)
  );
  const weekly = event?.tier === 'semanal';
  return state.cartelas.filter(c =>
    c.status === 'active'
    && !(weekly && c.weeklyEventId)
    && !(state.config.onePerLead && winners.has(c.leadId))
  );
}

export async function pickWinner(pool: Cartela[], prizeId: string, seed: string): Promise<{ winner: Cartela; idx: number }> {
  const h = await sha256(seed + '|' + prizeId);
  const idx = parseInt(h.slice(0, 13), 16) % pool.length;   // 52 bits → seguro em JS
  return { winner: pool[idx] as Cartela, idx };
}

/** Ordem fixa de abertura das posições. */
export function revealOrder(digits: number, order: RevealOrder): number[] {
  const o = [...Array(digits).keys()];
  return order === 'rtl' ? o.reverse() : o;
}

/**
 * O duelo final. O ganhador JÁ foi escolhido com a semente; esta função só decide
 * em que ordem as posições se abrem: direção de palco, não resultado. Deixa para o
 * fim a posição que mantém mais cartelas vivas, para o último dígito chegar com
 * dois, três ou quatro finalistas. Determinística: mesmo bolão e mesmo ganhador,
 * mesma ordem.
 */
export function dramaticOrder(pool: Cartela[], target: string, digits: number): { order: number[]; finalists: number } {
  const at = (c: Cartela, p: number): string => c.number.padStart(digits, '0')[p] as string;

  // 1. posição guardada para o final: a que deixa mais cartelas vivas
  let last = 0, finalists = -1;
  for (let p = 0; p < digits; p++) {
    let alive = 0;
    for (const c of pool) {
      let ok = true;
      for (let q = 0; q < digits; q++) { if (q !== p && at(c, q) !== target[q]) { ok = false; break; } }
      if (ok) alive++;
    }
    if (alive > finalists) { finalists = alive; last = p; }
  }

  // 2. as demais ordenadas para a queda ser geométrica: cada dígito elimina gente
  const rest = [...Array(digits).keys()].filter(p => p !== last);
  const order: number[] = [];
  const N = pool.length, F = Math.max(2, finalists);
  const ratio = rest.length ? Math.pow(F / Math.max(F, N), 1 / rest.length) : 1;
  let current = pool;

  for (let k = 1; k <= rest.length; k++) {
    const goal = Math.max(F, N * Math.pow(ratio, k));
    let pick = -1, pickSet = current, err = Infinity;
    for (const p of rest) {
      if (order.indexOf(p) >= 0) continue;
      const set = current.filter(c => at(c, p) === target[p]);
      const e = Math.abs(Math.log(Math.max(1, set.length)) - Math.log(Math.max(1, goal)));
      if (e < err) { err = e; pick = p; pickSet = set; }
    }
    if (pick < 0) break;
    order.push(pick); current = pickSet;
  }
  order.push(last);
  return { order, finalists };
}

/** Cartelas que continuam vivas depois de reveladas certas posições. */
export function survivors(pool: Cartela[], target: string, positions: number[], digits: number): Cartela[] {
  return pool.filter(c => {
    const n = c.number.padStart(digits, '0');
    return positions.every(p => n[p] === target[p]);
  });
}
